import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { memo, useCallback, useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useNavigate } from '@/libs/router'
import type { ColumnDef } from '@/libs/table'
import { actionsColumnBase, stickyNameClass, stickyNameCellClass } from '@/components/table-styles'
import type { ColumnMeta } from '@/components/table-styles'
import type { CompanyWorkflow, WorkflowStatus } from '../domain/workflow'

type WorkflowColumnMeta = ColumnMeta & {
	skeleton?: { type: 'text' | 'badge'; width?: string }
}

const statusVariant: Record<Exclude<WorkflowStatus, 'deleted'>, 'default' | 'secondary' | 'outline'> = {
	active: 'default',
	draft: 'secondary',
	archived: 'outline',
}

const NameCell = memo(({ name }: { name: string }) => <span className='truncate font-medium'>{name}</span>)
NameCell.displayName = 'NameCell'

const StatusCell = memo(({ status }: { status: WorkflowStatus }) => {
	const content = useIntlayer('workflows-list-page')
	const label = content.status[status as keyof typeof content.status]?.value ?? status
	const variant = statusVariant[status as keyof typeof statusVariant] ?? 'outline'
	return <Badge variant={variant}>{label}</Badge>
})
StatusCell.displayName = 'StatusCell'

const PhasesCell = memo(({ count }: { count: number }) => (
	<span className='text-muted-foreground tabular-nums'>{count}</span>
))
PhasesCell.displayName = 'PhasesCell'

const DescriptionCell = memo(({ description }: { description: string | null }) => (
	<span className='truncate text-muted-foreground text-sm'>{description ?? '-'}</span>
))
DescriptionCell.displayName = 'DescriptionCell'

const ActionsCell = memo(
	({
		workflowId,
		onArchive,
		onDelete,
	}: {
		workflowId: string
		onArchive: (id: string) => void
		onDelete: (id: string) => void
	}) => {
		const content = useIntlayer('workflows-list-page')
		const navigate = useNavigate()

		const handleEdit = useCallback(() => {
			navigate({ to: '/tasks/workflows/$workflowId/edit', params: { workflowId } })
		}, [workflowId, navigate])

		const handleArchive = useCallback(() => onArchive(workflowId), [workflowId, onArchive])
		const handleDelete = useCallback(() => onDelete(workflowId), [workflowId, onDelete])

		return (
			<div className='flex items-center justify-center w-full'>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' className='h-8 w-8 p-0'>
							<Icons.MoreHoriz className='h-4 w-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem onClick={handleEdit}>{content.actions.edit}</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={handleArchive}>{content.actions.archive}</DropdownMenuItem>
						<DropdownMenuItem onClick={handleDelete} className='text-FF3638'>
							{content.actions.delete}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		)
	},
)
ActionsCell.displayName = 'ActionsCell'

type ColumnMeta2 = { archiveWorkflow?: (id: string) => void; deleteWorkflow?: (id: string) => void }

const useWorkflowColumns = () => {
	const content = useIntlayer('workflows-list-page')
	const c = content.columns

	return useMemo<ColumnDef<CompanyWorkflow, unknown>[]>(
		() => [
			{
				id: 'name',
				accessorKey: 'name',
				header: c.name.value,
				size: 280,
				minSize: 200,
				enableResizing: true,
				meta: {
					headerLabel: c.name.value,
					sticky: true,
					skeleton: { type: 'text', width: 'w-40' },
					headerClassName: `w-[280px] min-w-[200px] md:sticky md:left-0 bg-background z-10 ${stickyNameClass}`,
					className: `w-[280px] min-w-[200px] md:sticky md:left-0 bg-background group-hover:bg-hover-group  z-10 ${stickyNameCellClass}`,
				} satisfies WorkflowColumnMeta,
				cell: ({ row }) => <NameCell name={row.original.name} />,
			},
			{
				id: 'status',
				accessorKey: 'status',
				header: c.status.value,
				size: 120,
				minSize: 100,
				enableResizing: true,
				meta: {
					headerLabel: c.status.value,
					skeleton: { type: 'badge' },
					className: 'w-[120px] min-w-[100px]',
				} satisfies WorkflowColumnMeta,
				cell: ({ row }) => <StatusCell status={row.original.status} />,
			},
			{
				id: 'phases',
				accessorFn: row => row.phases.length,
				header: c.phases.value,
				size: 100,
				minSize: 80,
				enableResizing: true,
				meta: {
					headerLabel: c.phases.value,
					skeleton: { type: 'text', width: 'w-6' },
					className: 'w-[100px] min-w-[80px]',
				} satisfies WorkflowColumnMeta,
				cell: ({ row }) => <PhasesCell count={row.original.phases.length} />,
			},
			{
				id: 'description',
				accessorKey: 'description',
				header: c.description.value,
				size: 340,
				minSize: 200,
				enableResizing: true,
				meta: {
					headerLabel: c.description.value,
					skeleton: { type: 'text', width: 'w-48' },
					className: 'w-[340px] min-w-[200px]',
				} satisfies WorkflowColumnMeta,
				cell: ({ row }) => <DescriptionCell description={row.original.description} />,
			},
			{
				...actionsColumnBase,
				header: c.actions.value,
				meta: {
					...actionsColumnBase.meta,
					headerLabel: c.actions.value,
					skeleton: { type: 'text' },
				} satisfies WorkflowColumnMeta,
				cell: ({ row, table }) => {
					const meta = table.options.meta as ColumnMeta2 | undefined
					return (
						<ActionsCell
							workflowId={row.original.id}
							onArchive={meta?.archiveWorkflow ?? (() => {})}
							onDelete={meta?.deleteWorkflow ?? (() => {})}
						/>
					)
				},
			},
		],
		[c],
	)
}

export { useWorkflowColumns }
export type { WorkflowColumnMeta as ColumnMeta }
